import React from 'react'

import { GatoHeader, Loading } from '@elgatoylacaja/components'
import {
  GatoLogoProvider,
  LoadingProvider,
  useGatoLogo,
  useIsMobile,
  useLoader,
  useScrollTop,
  useWindowSize,
} from '@elgatoylacaja/hooks'

import {
  createStyles,
  Grid,
  Theme,
  WithStyles,
  withStyles,
} from '@material-ui/core'

import {
  RouteComponentProps,
  HashRouter as Router,
  withRouter,
} from 'react-router-dom'

import { AppContext, AppContextProvider } from './contexts/AppContext'

import SwitchRoutes from './LazySwitchRoutes'

const styles = (theme: Theme) =>
  createStyles({
    app: {
      textAlign: 'center',
      maxWidth: '800px',
      width: '100%',
      margin: '0 auto',
      padding: '0',
      minHeight: '100vh',
    },
    container: {
      padding: 24,
      paddingTop: 0,
      position: 'relative',
      width: '800px',
      '@media only screen and (max-width : 800px)': {
        width: '100%',
      },
      display: 'flex',
      flexDirection: 'column',
    },
    logo: {
      height: 60,
      display: 'block',
      margin: '0px auto',
      paddingTop: '24px',
    },
    labs: {
      display: 'block',
      width: '250px',
      margin: '30px auto',
      '@media only screen and (max-width : 800px)': {
        display: 'none',
      },
    },
  })

const Loader = () => {
  const { loading } = useLoader()
  return <Loading fixed loading={loading} />
}

const showGatoLogoUrls = [
  '/terms',
  '/intro',
  '/primera-fase',
  '/regressors',
  '/thanks',
].flatMap(x => [x, `/demo${x}`])

const Container: React.FC<RouteComponentProps &
  WithStyles<typeof styles>> = props => {
  useScrollTop(props.location.pathname)

  const { shouldShowGatoLogo } = useGatoLogo(
    showGatoLogoUrls,
    props.location.pathname
  )

  const { height } = useWindowSize()

  const minHeight = height !== undefined ? `${height}px` : `100vh`

  return (
    <>
      {shouldShowGatoLogo ? <GatoHeader /> : null}
      <Grid
        container
        justify="center"
        style={{
          minHeight: shouldShowGatoLogo
            ? `calc(${minHeight} - 84px)`
            : `calc(${minHeight})`,
        }}
      >
        <Grid item className={props.classes.container}>
          {props.children}
        </Grid>
      </Grid>
    </>
  )
}

const FinalContainer = withRouter(Container)

function dragElement(elmnt: HTMLElement) {
  var pos1 = 0,
    pos2 = 0,
    pos3 = 0,
    pos4 = 0
  if (document.getElementById(elmnt.id + 'header')) {
    document.getElementById(elmnt.id + 'header')!.onmousedown = dragMouseDown
  } else {
    elmnt.onmousedown = dragMouseDown
  }

  function dragMouseDown(e: MouseEvent) {
    e = e || window.event
    e.preventDefault()
    pos3 = e.clientX
    pos4 = e.clientY
    document.onmouseup = closeDragElement
    document.onmousemove = elementDrag
  }

  function elementDrag(e: MouseEvent) {
    e = e || window.event
    e.preventDefault()
    // calculate the new cursor position:
    pos1 = pos3 - e.clientX
    pos2 = pos4 - e.clientY
    pos3 = e.clientX
    pos4 = e.clientY
    // set the element's new position:
    elmnt.style.top = elmnt.offsetTop - pos2 + 'px'
    elmnt.style.left = elmnt.offsetLeft - pos1 + 'px'
  }

  function closeDragElement() {
    // stop moving when mouse button is released:
    document.onmouseup = null
    document.onmousemove = null
  }
}

const DebugWindow = () => {
  const {
    appState: {
      experiment: { config },
    },
  } = React.useContext(AppContext)

  const divRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    if (divRef.current !== null) {
      dragElement(divRef.current)
    }
  }, [divRef])

  const { isMobile } = useIsMobile()

  return isMobile ? (
    <></>
  ) : (
    <div
      ref={divRef}
      style={{
        position: 'absolute',
        width: '400px',
        height: '120px',
        overflow: 'hidden',
        backgroundColor: 'hsl(209 49% 15% / 0.9)',
        borderRadius: '5px',
        padding: '5px',
        boxSizing: 'border-box',
        color: 'white',
        right: 0,
        bottom: '20px',
        textAlign: 'left',
        cursor: 'grab',
      }}
    >
      {config.map((c, i) => {
        return (
          <div key={i}>
            <b>
              {i + 1}. {c.dilema.title}
            </b>
            : {c.tipo}
          </div>
        )
      })}
    </div>
  )
}

const App = ({ classes }: WithStyles<typeof styles>) => {
  return (
    <div className={classes.app}>
      <AppContextProvider>
        <LoadingProvider>
          <GatoLogoProvider>
            <Router hashType="slash">
              <Loader />
              <FinalContainer classes={classes}>
                <SwitchRoutes />
                {process.env.REACT_APP_ENVIRONMENT !== 'production' && (
                  <DebugWindow />
                )}
              </FinalContainer>
            </Router>
          </GatoLogoProvider>
        </LoadingProvider>
      </AppContextProvider>
    </div>
  )
}

export default withStyles(styles)(App)
